import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  DataSourceStatus,
  DatasourceInstructionMode,
} from '@/lib/graphql/generated/graphql';
import { useErrorToastHandler } from '@/lib/hooks/useErrorToastHandler';
import { useUploadCsv } from '@/lib/hooks/useUploadCsv';
import { pauseCouponCsvRefetchState } from '@/states/pause-coupon-csv-refetch.state';
import { useSetAtom } from 'jotai';
import { Upload } from 'lucide-react';

type CSVImportDigitalProductsButtonProps = {
  dealId: string;
  onUploadSuccess: (importedGtins: string[]) => Promise<void>;
  buttonText?: string;
  /** Pause global refetch (SSE `DEAL` signal) during digital product CSV import */
  pauseRefetch?: boolean;
  instructionMode?: DatasourceInstructionMode;
};

export function CSVImportDigitalProductsButton({
  dealId,
  onUploadSuccess,
  buttonText,
  pauseRefetch = false,
  instructionMode = DatasourceInstructionMode.CreateOnly,
}: CSVImportDigitalProductsButtonProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const setPauseRefetch = useSetAtom(pauseCouponCsvRefetchState);
  const [isFetchingLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const {
    addCsvDataSourceToDeal,
    errors,
    dataSourceStatus,
    domainError,
    isDomainError,
  } = useUploadCsv(dealId, instructionMode);

  const [importedGtins, setImportedGtins] = useState<string[]>([]);

  useErrorToastHandler({
    domainError,
    isDomainError,
    error: errors ?? null,
    domainToast: {
      titleKey: 'general.error_import_csv',
      defaultMessageKey: 'general.error_import_csv_message',
    },
    genericToast: {
      titleKey: 'general.error_import_csv',
    },
  });

  const parseGtinsFromCsv = async (file: File): Promise<string[]> => {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length === 0) return [];
    const header = lines[0];
    const delimiter =
      header.includes(';') &&
      header.split(';').length > header.split(',').length
        ? ';'
        : ',';
    const headers = header.split(delimiter).map((h) => h.trim().toLowerCase());
    const gtinIndex = headers.findIndex((h) => h === 'gtin');
    if (gtinIndex === -1) return [];
    const gtins: string[] = [];
    for (let i = 1; i < lines.length; i += 1) {
      const cols = lines[i].split(delimiter);
      const value = (cols[gtinIndex] || '').trim();
      if (value) gtins.push(value);
    }
    // Uniques et non vides
    return Array.from(new Set(gtins.filter(Boolean)));
  };

  useEffect(() => {
    // Empêcher le comportement par défaut du drag and drop sur le document
    const preventDefaults = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Ajouter les écouteurs d'événements au document
    document.addEventListener('dragover', preventDefaults);
    document.addEventListener('drop', preventDefaults);

    // Nettoyer les écouteurs lors du démontage du composant
    return () => {
      document.removeEventListener('dragover', preventDefaults);
      document.removeEventListener('drop', preventDefaults);
    };
  }, []);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsLoading(true);

    try {
      if (pauseRefetch) setPauseRefetch(true);
      // Parse GTINs avant upload
      const gtins = await parseGtinsFromCsv(file);
      setImportedGtins(gtins);
      const result = await addCsvDataSourceToDeal(file);
      if (result.success) {
        await onUploadSuccess(gtins);
      }
      // En cas d'échec, useErrorToastHandler affiche le toast via domainError/errors
    } catch (error) {
      toast({
        title: t('general.error_import_csv'),
        description:
          error instanceof Error ? error.message : t('general.error_unknown'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
      // Reset the input
      event.target.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file || !file.name.endsWith('.csv')) {
      toast({
        title: 'Format incorrect',
        description: 'Veuillez fournir un fichier CSV',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      if (pauseRefetch) setPauseRefetch(true);
      const gtins = await parseGtinsFromCsv(file);
      setImportedGtins(gtins);
      const result = await addCsvDataSourceToDeal(file);
      if (result.success) {
        await onUploadSuccess(gtins);
      }
    } catch (error) {
      toast({
        title: t('general.error_import_csv'),
        description:
          error instanceof Error ? error.message : t('general.error_unknown'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (dataSourceStatus === DataSourceStatus.Processed) {
      onUploadSuccess(importedGtins).finally(() => {
        if (pauseRefetch) setPauseRefetch(false);
      });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- pauseRefetch/setPauseRefetch only used in .finally(); including them would re-run when pauseRefetch toggles and could double-invoke onUploadSuccess
  }, [dataSourceStatus, onUploadSuccess, importedGtins]);

  const isLoading =
    isFetchingLoading ||
    dataSourceStatus === DataSourceStatus.Pending ||
    dataSourceStatus === DataSourceStatus.Created ||
    dataSourceStatus === DataSourceStatus.Imported;

  return (
    <div
      className={`border-2 border-dashed ${
        isDragging ? 'border-primary bg-primary/5' : 'border-gray-300'
      } rounded-md p-6 text-center transition-colors`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Upload
        className={`mx-auto h-12 w-12 ${
          isDragging ? 'text-primary' : 'text-gray-400'
        }`}
      />
      <p className="mt-2 text-sm text-gray-500">
        {t('general.drag_drop_csv_file')}
      </p>
      <div className="relative">
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          disabled={isLoading}
        />
        <Button variant="outline" disabled={isLoading}>
          <Upload className="mr-2 h-4 w-4" />
          {isLoading
            ? t('general.importation_in_progress')
            : (buttonText ?? t('general.import'))}
        </Button>
      </div>
    </div>
  );
}
